import { format } from 'date-fns';
import { Bar, BarChart, Text as RechartsText, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { Box, Spinner, Text, TextButton } from '@zillow/constellation-10';
import { IconExternalOutline } from '@zillow/constellation-icons';

import { useRentalManagerGrowthGetCompStatsForListingWebApiHandler_getCompStats } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { formatAddress } from '@zg-rentals/zillow-rental-manager/utils';

import numberUtils from '../../../utils/numberUtils';

import { statisticsComponentStyles } from '../styles';

import type { GetUserPropertyOverviewAddressWebDto } from '@zg-rentals/ts-rental-manager-api-client';

const PMR_LANDING_PAGE = 'https://www.zillow.com/rental-manager/price-my-rental/';
const PMR_RESULTS_BASE = 'https://www.zillow.com/rental-manager/price-my-rental/results/';

// offset for the chart's top margin in order to get the label above the bars to display
const CHART_CONFIG = {
  OFFSET_TOP: 20,
  BAR_GAP: 8,
  BAR_SIZE: 16,
};

// bar layout starts to get funky if less than these values
// real height of the chart is set via css: StyledBarChartContainer
const CHART_HEIGHT = {
  BASE: 100,
};

const CHART_LABEL = {
  INQUIRIES: 'Inquiries',
  VIEWS: 'Views',
};

const BAR_NAME = {
  MY_LISTINGS: 'Your Listing',
  COMPARABLES: 'Comparables',
};

const BAR_FILL = {
  PURPLE: '#6000b6',
  LIGHT_BLUE: '#00c3dc',
};

type Props = {
  listingAlias: string;
  address: GetUserPropertyOverviewAddressWebDto | undefined;
  hasAnalyticsRentComps?: boolean;
};

const Statistics = ({ listingAlias, address, hasAnalyticsRentComps = false }: Props) => {
  const { data, isLoading, error } = useRentalManagerGrowthGetCompStatsForListingWebApiHandler_getCompStats(
    {
      listingAlias,
    },
    {
      enabled: Boolean(listingAlias),
      meta: {
        tags: ['listingCompStats'],
        context: { api: 'rentalManagerGrowth', details: 'Statistics' },
      },
    },
  );

  if (isLoading) {
    return (
      <Box css={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
        <Spinner size="sm" />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box css={{ overflow: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Text textStyle="body-sm">
          {error ? (error as Error).message : 'There was an error fetching listing statistics.'}
        </Text>
      </Box>
    );
  }

  const { listingStats, similarFloorplansStats } = data;

  if (!listingStats || !similarFloorplansStats) {
    return (
      <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Text textStyle="body-sm">Statistics are not available for this listing.</Text>
      </Box>
    );
  }

  const { startDate, endDate, viewsTotal: myListingViewsTotal, inquiriesTotal: myListingInquiriesTotal } = listingStats;
  const { viewsTotal: compViewsTotal, inquiriesTotal: compInquiriesTotal } = similarFloorplansStats;

  const startDateFormatted = format(new Date(startDate), 'MM/dd/yy');
  const endDateFormatted = format(new Date(endDate), 'MM/dd/yy');
  const title = `Activity for ${startDateFormatted}–${endDateFormatted}`;

  const viewsData = [
    {
      name: CHART_LABEL.VIEWS,
      [BAR_NAME.MY_LISTINGS]: myListingViewsTotal,
      [BAR_NAME.COMPARABLES]: compViewsTotal,
    },
  ];

  const inquiriesData = [
    {
      name: CHART_LABEL.INQUIRIES,
      [BAR_NAME.MY_LISTINGS]: myListingInquiriesTotal,
      [BAR_NAME.COMPARABLES]: compInquiriesTotal,
    },
  ];

  const { formattedKebobCase } = formatAddress((address ?? {}) as unknown as Record<string, string>);
  const pmrHref = hasAnalyticsRentComps ? PMR_LANDING_PAGE : `${PMR_RESULTS_BASE}${formattedKebobCase}/`;
  const pmrUrl = new URL(pmrHref);
  if (address?.unit) pmrUrl.searchParams.set('unit', address.unit);

  const ctaText = hasAnalyticsRentComps ? 'Get rent comps' : 'Compare to your property';

  // @ts-expect-error recharts label render props are not typed
  const returnCustomBarLabel = (rechartProps) => {
    const { fill, label, value, viewBox, y } = rechartProps;
    const { width } = viewBox;
    const calcX = parseInt(width, 10) + 10;
    const response = `${numberUtils.formatWithK(value)} • ${label}`;

    return (
      <RechartsText
        x={calcX}
        y={y}
        dy={12}
        fontSize="12"
        fontFamily="OpenSans, sans-serif"
        fill={fill}
        // @ts-expect-error recharts position prop is not reflected in TypeScript types
        position="right"
      >
        {response}
      </RechartsText>
    );
  };

  // @ts-expect-error recharts label render props are not typed
  const renderCustomXAxisLabel = (rechartProps) => {
    const { label } = rechartProps;

    return (
      <>
        {/* @ts-expect-error */}
        <RechartsText x={0} y={15} dy={0} fontSize="16" fontFamily="sans-serif" position="right">
          {label}
        </RechartsText>
      </>
    );
  };

  // @ts-expect-error recharts data is not typed
  const getBuffer = (bufferData) => {
    const obj = bufferData[0];
    const maxVal = Math.max(obj[BAR_NAME.MY_LISTINGS], obj[BAR_NAME.COMPARABLES]).toString().split('');

    let bufferString = '5';
    maxVal.forEach(() => {
      bufferString += '0';
    });

    // remove last digit
    bufferString = bufferString.slice(0, -1);

    return `dataMax + ${bufferString}`;
  };

  // @ts-expect-error recharts data is not typed
  const renderBarChart = (barChartData, label) => {
    if (!barChartData || !label) return null;

    // set false to fix label sometimes not displaying on load
    // ref: https://github.com/recharts/recharts/issues/1135
    const isAnimationActive = false;

    return (
      <ResponsiveContainer
        className={`ResponsiveContainer-BarChart ResponsiveContainer-BarChart-${label}`}
        width="100%"
        height={CHART_HEIGHT.BASE}
        debounce={0}
      >
        <BarChart
          data={barChartData}
          layout="vertical"
          margin={{ top: CHART_CONFIG.OFFSET_TOP, right: 0, left: 0, bottom: 0 }}
          barGap={CHART_CONFIG.BAR_GAP}
          barSize={CHART_CONFIG.BAR_SIZE}
          stackOffset="sign"
        >
          <XAxis
            type="number"
            hide={false}
            axisLine={false}
            tick={false}
            domain={[0, getBuffer(barChartData)]}
            label={{ content: renderCustomXAxisLabel, label }}
          />
          <YAxis type="category" dataKey="name" hide={true} />
          <Bar
            dataKey={BAR_NAME.MY_LISTINGS}
            fill={BAR_FILL.PURPLE}
            minPointSize={1}
            // @ts-expect-error recharts label prop is not typed
            label={{ content: returnCustomBarLabel, label: BAR_NAME.MY_LISTINGS }}
            isAnimationActive={isAnimationActive}
          />
          <Bar
            dataKey={BAR_NAME.COMPARABLES}
            fill={BAR_FILL.LIGHT_BLUE}
            minPointSize={1}
            // @ts-expect-error recharts label prop is not typed
            label={{ content: returnCustomBarLabel, label: BAR_NAME.COMPARABLES }}
            isAnimationActive={isAnimationActive}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Box css={statisticsComponentStyles} className="Statistics">
      <Text asChild textStyle="body-sm-bold" css={{ marginBottom: '200' }}>
        <p>{title}</p>
      </Text>
      <Box className="BarChart-Container">
        {renderBarChart(viewsData, CHART_LABEL.VIEWS)}
        {renderBarChart(inquiriesData, CHART_LABEL.INQUIRIES)}
      </Box>

      <Box css={{ display: 'flex', justifyContent: 'center', marginTop: '400' }}>
        <TextButton asChild icon={<IconExternalOutline />} iconPosition="end">
          <a
            href={pmrUrl.toString()}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={`${ctaText} (opens in new tab)`}
          >
            {ctaText}
          </a>
        </TextButton>
      </Box>
    </Box>
  );
};

export default Statistics;
